import { useEffect, useState, useCallback } from 'react';
import queryString from 'query-string';
import { elementScrollIntoViewPolyfill } from 'seamless-scroll-polyfill';
import { EPISODE_LIST_STATE } from '../constants';
import { AnchorAPI } from '../../../modules/AnchorAPI';
import { useCurrentUserCtx } from '../../../contexts/CurrentUser';
import {
  EpisodeItem,
  FetchEpisodesResponse,
  ORDER_BY,
  FILTER_BY,
} from '../../../modules/AnchorAPI/v3/episodes/fetchEpisodes';
import {
  PaginationParam,
  PageToken,
  HandleFetchEpisodesParams,
  PaginationUrls,
} from '../types';
import serverRenderingUtils from '../../../../helpers/serverRenderingUtils';

export const EPISODES_PER_PAGE = 15;
const INTIAL_PAGINATION_TOKENS_AND_URLS = {
  nextPage: { pageToken: undefined, url: null },
  prevPage: { pageToken: undefined, url: null },
  firstPage: { pageToken: undefined, url: null },
  lastPage: { pageToken: undefined, url: null },
};

/**
 * The web currently does not support e.g. viewing trailer details, so we filter them out.
 */
const filterEpisode = (episode: EpisodeItem): boolean => !episode.isTrailer;

/**
 * on mount, this hook is responsible for fetching the list of episodes (depends on web station ID)
 */
export function useEpisodeList() {
  const {
    state: { userId, webStationId, error: currentUserError },
  } = useCurrentUserCtx();

  const [episodeListState, setEpisodeListState] = useState<EPISODE_LIST_STATE>(
    EPISODE_LIST_STATE.IDLE
  );
  const [episodes, setEpisodes] = useState<EpisodeItem[]>([]);
  const [pageTokensAndUrls, setPageTokens] = useState<PaginationUrls>(
    INTIAL_PAGINATION_TOKENS_AND_URLS
  );
  const [orderBy, setOrderBy] = useState<ORDER_BY>(ORDER_BY.PUBLISH_ON);
  const [currentPageToken, setCurrentPageToken] = useState<
    PageToken | undefined
  >(undefined);
  const [filterBy, setFilterBy] = useState<FILTER_BY>(FILTER_BY.ALL);

  const handleFetchEpisodes = useCallback(
    (
      {
        pageToken,
        orderBy: newOrderBy,
        filterBy: newFilterBy,
        scrollToTop,
      }: HandleFetchEpisodesParams = {
        pageToken: undefined,
        orderBy: ORDER_BY.PUBLISH_ON,
        filterBy: FILTER_BY.ALL,
        scrollToTop: false,
      }
    ) => {
      if (webStationId) {
        setEpisodeListState(EPISODE_LIST_STATE.LOADING_EPISODES);
        if (scrollToTop) smoothScrollToTop();
        if (newFilterBy) setFilterBy(newFilterBy);
        AnchorAPI.fetchEpisodes({
          webStationId,
          pageToken,
          orderBy: newOrderBy,
          filterBy: newFilterBy,
          limit: EPISODES_PER_PAGE,
        })
          .then(({ items, next, prev, first, last }) => {
            setPageTokens(
              getPageTokens({
                next,
                prev,
                first,
                last,
              })
            );
            setEpisodes(items.filter(filterEpisode));
            setEpisodeListState(EPISODE_LIST_STATE.SUCCESS);
            if (newOrderBy) setOrderBy(newOrderBy);
            setCurrentPageToken(pageToken);
          })
          .catch(() => {
            setEpisodeListState(EPISODE_LIST_STATE.FAILED);
          });
      }
    },
    [webStationId]
  );

  function handleRefetchCurrentPage() {
    handleFetchEpisodes({
      pageToken: currentPageToken,
      orderBy,
      filterBy,
      scrollToTop: true,
    });
  }

  useEffect(() => {
    if (currentUserError) setEpisodeListState(EPISODE_LIST_STATE.FAILED);
  }, [currentUserError]);

  useEffect(() => {
    if (webStationId) handleFetchEpisodes();
  }, [webStationId, handleFetchEpisodes]);

  useEffect(() => {
    // polyfill native scrollIntoView for iOS
    elementScrollIntoViewPolyfill();
  }, []);

  return {
    userId,
    webStationId,
    episodes,
    episodeListState,
    pageTokensAndUrls,
    orderBy,
    filterBy,
    handleFetchEpisodes,
    handleRefetchCurrentPage,
  };
}

/**
 * the API conveniently returns the URL a client would need to do a request with
 * in order to get the first/last/next/prev page of episodes. the important
 * piece of info in the URL is the `pageToken` search param. unfortunately,
 * public-web needs to first go through the proxy, so we'll need to parse the
 * URL and grab the token (if present) and then do the request through our
 * typical proxy method.
 */
export function getPageToken(urlPath: string | null): PageToken {
  if (!urlPath || !urlPath.includes('?')) return undefined;
  const [, queryParams] = urlPath.split('?');
  const params: PaginationParam = queryString.parse(queryParams);
  if (Object.keys(params).includes('pageToken')) {
    return (params.pageToken as PageToken) || undefined;
  }
  return undefined;
}

function getPageTokens({
  next,
  prev,
  first,
  last,
}: Pick<
  FetchEpisodesResponse,
  'first' | 'last' | 'next' | 'prev'
>): PaginationUrls {
  return {
    nextPage: { pageToken: getPageToken(next), url: next },
    prevPage: { pageToken: getPageToken(prev), url: prev },
    firstPage: { pageToken: getPageToken(first), url: first },
    lastPage: { pageToken: getPageToken(last), url: last },
  };
}
function smoothScrollToTop() {
  if (!serverRenderingUtils.windowUndefined()) {
    const headerElement = document.getElementById('app');
    if (headerElement) {
      headerElement.scrollIntoView({ behavior: 'smooth' });
    }
  }
}
